import { FC, useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';

import MobileLogo from 'app/components/MobileLogo';
import Logo from 'app/components/shared/Logo';

import Logout from './AppLayout/Logout';
import NavContainer from './AppLayout/NavContainer';
import NavWrapper from './AppLayout/NavWrapper';
import Wrapper from './AppLayout/Wrapper';
import AriaClickHandler from '../v2/components/AriaClickHandler';
import IconButton from '../v2/components/IconButton';
import ThreeEvenElements from '../v2/components/ThreeEvenElements';
import Tooltip from '../v2/components/Tooltip';

// Local Typings
interface Props {
  withLogoutButton?: boolean;
}

// Local Variables
function redirectToHomePage() {
  // This page might be used outside of Router, so we are doing a hard reload
  window.location.href = window.location.origin;
}

// Component Definition
const EmptyNavBar: FC<Props> = props => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLSpanElement>(null);
  const [navHeight, setNavHeight] = useState(0);
  const { t } = useTranslation('components');

  useEffect(() => {
    const resizeObserver = new ResizeObserver(entries => {
      const [entry] = entries;

      const { height = 0 } = entry?.contentRect ?? {};

      setNavHeight(height);
    });

    if (wrapperRef.current) {
      resizeObserver.observe(wrapperRef.current);
    }

    return () => {
      if (wrapperRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        resizeObserver.unobserve(wrapperRef.current);
      }
    };
  },
  [wrapperRef]);

  return (
    <Wrapper
      navHeight={navHeight}
      ref={wrapperRef}
    >
      <NavWrapper>
        <NavContainer>
          <ThreeEvenElements
            centerElement={(
              <AriaClickHandler
                ariaLabel={t('emptyNavBar.homeAria', 'home')}
                onClick={redirectToHomePage}
                role="link"
              >
                <span>
                  {isMobile ? <MobileLogo /> : <Logo variant="light" />}
                </span>
              </AriaClickHandler>
            )}
            paddingY={6}
            rightElement={props.withLogoutButton && (
              <Logout>
                {({ onLogout }) => (
                  <Tooltip
                    childRef={buttonRef}
                    noWrap
                    tooltipLabel={t('emptyNavBar.signOut', 'Sign Out')}
                    tooltipPlacement="bottom"
                  >
                    <span ref={buttonRef}>
                      <IconButton
                        iconColor="white"
                        iconName="exit"
                        onClick={onLogout}
                      />
                    </span>
                  </Tooltip>
                )}
              </Logout>
            )}
          />
        </NavContainer>
      </NavWrapper>
    </Wrapper>
  );
};

export default EmptyNavBar;
